/* eslint-disable ember/classic-decorator-hooks, ember/classic-decorator-no-classic-methods, ember/no-computed-properties-in-native-classes, ember/no-get, ember/require-computed-property-dependencies, no-restricted-properties, warp-drive/no-legacy-request-patterns */
import Controller from '@ember/controller';
import { tracked } from '@glimmer/tracking';
import { action, computed } from '@ember/object';
import { service } from '@ember/service';
import Jurisdiction from '@linc-technologies/linced-common/enums/jurisdiction';
import { sortRubricFamilies } from '@linc-technologies/linced-common/utils/rubrics';
import { apiErrorDetail } from 'linced-admin/utils/api-error-detail';

export default class RubricController extends Controller {
  // Services
  @service
  store;

  @service
  toaster;

  @service
  router;

  Jurisdiction = Jurisdiction;

  @tracked
  editingTemplateSet = null;

  @action
  setEditingTemplateSet(value) {
    this.editingTemplateSet = value;
  }

  @tracked
  editingRubric = null;

  @tracked
  editingElements = [];

  @tracked
  selectedElementType = null;

  DEFAULT_COLUMNS = Object.freeze([
    {
      column: 1,
      label: 'Emerging',
      colour: '#e91e90',
      colourClass: 'emerging',
    },
    {
      column: 2,
      label: 'Developing',
      colour: '#ff9800',
      colourClass: 'developing',
    },
    {
      column: 3,
      label: 'Consolidating',
      colour: '#4dd0b8',
      colourClass: 'consolidating',
    },
    {
      column: 4,
      label: 'Proficient',
      colour: '#00897b',
      colourClass: 'proficient',
    },
    {
      column: 5,
      label: 'Exceeding',
      colour: '#ba68c8',
      colourClass: 'exceeding',
    },
  ]);

  ELEMENT_TYPES = Object.freeze([
    {
      key: 'text',
      label: 'Text for teachers',
      placeholder: 'Criteria text...',
    },
    {
      key: 'achievedNarrative',
      label: 'Wording to add to the narrative',
      placeholder: 'Narrative wording...',
    },
    {
      key: 'helpAtHome',
      label: 'Help at Home wording',
      placeholder: 'Help at home wording...',
    },
    {
      key: 'nextStep',
      label: 'Next Step wording',
      placeholder: 'Next step wording...',
    },
  ]);

  init() {
    super.init(...arguments);
    this.editingElements = [];
  }

  @computed('editingRubric.columns.[]')
  get editingColumns() {
    let rubric = this.editingRubric;
    if (!rubric) {
      return [];
    }
    let columns = rubric.get('columns') || [];
    return columns.map((col) => {
      let idx = columns.indexOf(col);
      let defaults = this.DEFAULT_COLUMNS[idx];
      return {
        column: col.get('column'),
        label: col.get('label'),
        colour: defaults ? defaults.colour : '#999',
        colourClass: defaults ? defaults.colourClass : '',
      };
    });
  }

  @computed
  get sortedTemplateSets() {
    return (this.store.peekAll('milestones/set') || [])
      .slice()
      .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
  }

  @computed
  get sortedSubjects() {
    return (this.store.peekAll('goals/subject') || [])
      .slice()
      .sort((a, b) => (a.label || '').localeCompare(b.label || ''));
  }

  @computed
  get jurisdictionOptions() {
    return Jurisdiction.values;
  }

  @computed('editingRubric.jurisdiction')
  get editingJurisdiction() {
    let value = this.get('editingRubric.jurisdiction');
    return Jurisdiction.values.find((j) => j.value === value) || null;
  }

  @computed('editingRubric.jurisdiction')
  get canSaveRubric() {
    return !!this.get('editingRubric.jurisdiction');
    // Versioning temporarily disabled: rubric saves now mutate in place, so
    // `editingRubric.next` is never set. Restore when versioning returns:
    //   computed('editingRubric.{jurisdiction,next}', function() { … &&
    //     !this.get('editingRubric.next') })
  }

  // Every loaded rubric family; a live list, so new families show
  get allFamilies() {
    return this.store.peekAll('reports/rubric-family');
  }

  // Rubric families in the rubric's jurisdiction, default first
  @computed(
    'allFamilies.@each.{name,isDefault,jurisdiction}',
    'editingRubric.jurisdiction',
  )
  get familyOptions() {
    let jurisdiction = this.editingRubric?.jurisdiction;
    return sortRubricFamilies(
      this.allFamilies.filter(
        (f) => !f.isNew && !f.isDeleted && f.jurisdiction === jurisdiction,
      ),
    );
  }

  // Rubrics saved before families existed belong to the default family
  @computed('editingRubric.familyId', 'familyOptions.[]')
  get editingFamily() {
    let familyId = this.editingRubric?.familyId;
    return (
      (familyId
        ? this.familyOptions.find((f) => f.id === familyId)
        : this.familyOptions.find((f) => f.isDefault)) || null
    );
  }

  @computed('editingRubric.templateSubjectId')
  get editingSubject() {
    let subjectId = this.get('editingRubric.templateSubjectId');
    if (!subjectId) {
      return null;
    }
    return this.store.peekRecord('goals/subject', subjectId);
  }

  @computed('editingTemplateSet.{milestones.[],milestoneSetTemplateId}')
  get editingMilestones() {
    let set = this.editingTemplateSet;
    if (!set) {
      return [];
    }
    let msTemplateId = set.belongsTo('milestoneSetTemplateId').id();
    if (msTemplateId) {
      return this.store
        .peekAll('milestones/milestone')
        .filter(
          (m) => m.belongsTo('milestoneSetTemplateId').id() === msTemplateId,
        )
        .sort((a, b) =>
          a.sequence > b.sequence ? 1 : a.sequence < b.sequence ? -1 : 0,
        );
    }
    return (set.get('milestones') || [])
      .slice()
      .sort((a, b) => a.sequence - b.sequence);
  }

  async loadRubric(rubric) {
    if (rubric.jurisdiction) {
      this.store.query('reports/rubric-family', {
        jurisdiction: rubric.jurisdiction,
      });
    }
    let rubricId = rubric.get('id');

    let elements = await this.store.query('reports/rubric-element', {
      rubricId: rubricId,
      includeCriteria: true,
    });
    let criteria = this.store.peekAll('reports/rubric-criteria');

    let columns = rubric.get('columns') || [];
    let columnDefs = columns.map((col, idx) => {
      let defaults = this.DEFAULT_COLUMNS[idx];
      return {
        column: col.get('column'),
        label: col.get('label'),
        colour: defaults ? defaults.colour : '#999',
        colourClass: defaults ? defaults.colourClass : '',
      };
    });

    let sortedElements = elements
      .slice()
      .sort((a, b) => a.sequence - b.sequence);
    sortedElements.forEach((element) => {
      element.set('expanded', false);
      let elementId = element.get('id');
      let elementCriteria = criteria.filter(
        (c) => c.belongsTo('elementId').id() === elementId,
      );
      element.set(
        '_columns',
        columnDefs.map((colDef) => {
          let colCriteria = elementCriteria
            .filter((c) => c.get('progressColumn') === colDef.column)
            .sort((a, b) =>
              a.sequence > b.sequence ? 1 : a.sequence < b.sequence ? -1 : 0,
            );
          return {
            column: colDef.column,
            label: colDef.label,
            colour: colDef.colour,
            colourClass: colDef.colourClass,
            criteria: colCriteria,
          };
        }),
      );
    });

    let templateSet = null;
    let milestoneIds = rubric.get('templateMilestoneIds')?.toArray() ?? [];
    if (milestoneIds.length) {
      let milestone = this.store.peekRecord(
        'milestones/milestone',
        milestoneIds[0],
      );
      if (milestone) {
        templateSet = this.store.peekRecord(
          'milestones/set',
          milestone.belongsTo('templateSetId').id(),
        );
      }
    }

    this.editingRubric = rubric;
    this.editingTemplateSet = templateSet;
    this.editingElements = sortedElements;
    this.selectedElementType = this.ELEMENT_TYPES[0];
  }

  @action
  changeJurisdiction(option) {
    // Families are per jurisdiction, so a change of jurisdiction clears it
    this.set('editingRubric.jurisdiction', option ? option.value : null);
    this.set('editingRubric.familyId', '');
    this.notifyPropertyChange('editingJurisdiction');
    if (option) {
      this.store.query('reports/rubric-family', {
        jurisdiction: option.value,
      });
    }
  }

  @action
  changeFamily(family) {
    this.set('editingRubric.familyId', family?.id ?? '');
  }

  @action
  changeSubject(subject) {
    this.set(
      'editingRubric.templateSubjectId',
      subject ? subject.get('id') : null,
    );
    this.notifyPropertyChange('editingSubject');
  }

  @action
  toggleEditingMilestone(milestone) {
    let id = milestone.get('id');
    let ids = this.get('editingRubric.templateMilestoneIds');
    if (ids.includes(id)) {
      ids.removeObject(id);
    } else {
      ids.pushObject(id);
    }
  }

  @action
  addElement() {
    let rubric = this.editingRubric;
    let sequence = this.editingElements.length + 1;
    let element = this.store.createRecord('reports/rubric-element', {
      rubricId: rubric,
      name: 'New Element',
      sequence: sequence,
    });

    let cols = this.editingColumns || [];
    element.set(
      '_columns',
      cols.map((c) => ({
        column: c.column,
        label: c.label,
        colour: c.colour,
        colourClass: c.colourClass,
        criteria: [],
      })),
    );
    element.set('expanded', true);

    this.editingElements = [...this.editingElements, element];
  }

  @action
  removeElement(element) {
    // In-place save: mark the record (and its criteria) for deletion (new
    // records are discarded from the store; existing records are persisted
    // as a DELETE on the next saveRubric). The backend does not cascade,
    // so criteria must be deleted explicitly. Restore on revert to
    // versioning:
    //   this.editingElements.removeObject(element);
    for (let col of element.get('_columns') || []) {
      for (let criteria of col.criteria || []) {
        criteria.deleteRecord();
      }
    }
    element.deleteRecord();
    this.editingElements = this.editingElements.filter((el) => el !== element);
  }

  @action
  toggleElementExpanded(element) {
    element.toggleProperty('expanded');
  }

  @action
  updateElementName(element, name) {
    element.set('name', name);
  }

  @action
  addCriteria(element, col) {
    let rubric = this.editingRubric;
    let criteria = this.store.createRecord('reports/rubric-criteria', {
      rubricId: rubric,
      elementId: element,
      progressColumn: col.column,
      text: '',
      sequence: (col.criteria || []).length + 1,
    });

    // Rebuild col itself (not just col.criteria) so {{each element._columns
    // as |col|}}'s default @identity keying sees a changed item and
    // re-renders the nested {{each col.criteria}} block — mutating
    // col.criteria in place on the same col reference would leave the
    // criteria list stale in the UI until a full page reload.
    element.set(
      '_columns',
      (element.get('_columns') || []).map((c) =>
        c === col ? { ...c, criteria: [...(c.criteria || []), criteria] } : c,
      ),
    );
  }

  // `{{on "input"}}` gives the event, not the value, so read the value from
  // the event target.
  @action
  updateCriteriaText(criteria, event) {
    let key = this.selectedElementType.key;
    criteria.set(key, event.target.value);
  }

  @action
  removeCriteria(element, col, criteria) {
    // In-place save: mark for deletion as with removeElement. Restore on
    // revert to versioning:
    //   col.criteria.removeObject(criteria);
    criteria.deleteRecord();

    // See addCriteria's comment: rebuild col itself, not just col.criteria.
    element.set(
      '_columns',
      (element.get('_columns') || []).map((c) =>
        c === col
          ? { ...c, criteria: c.criteria.filter((item) => item !== criteria) }
          : c,
      ),
    );
  }

  @action
  async saveRubric() {
    let rubric = this.editingRubric;
    // Versioning temporarily disabled — the backend now mutates the rubric
    // in place and never returns a `next` pointer, so `targetRubric` is
    // always the rubric we just saved. Kept the variable name so the
    // element/criteria creation below reads cleanly. Restore when
    // versioning returns:
    //   let next;
    let targetRubric = rubric;
    let saved = false;
    try {
      if (rubric.get('hasDirtyAttributes')) {
        await rubric.save();
      }
      // next = rubric.get('next');
      //
      // if (next) {
      //   targetRubric = await this.store.findRecord('reports/rubric', next);
      // }

      // In-place save: persist mutations on the editingElements records
      // themselves (which ARE the live records loaded by loadRubric). For
      // isNew records this creates them; for existing records this updates.
      // Skip records that aren't dirty so a Save click doesn't fan out to a
      // PATCH per element + criterion when only one thing changed.
      // (hasDirtyAttributes is true for isNew records too, so this catches
      // both creates and updates.)
      for (let element of this.editingElements) {
        if (element.get('hasDirtyAttributes')) {
          if (element.get('isNew')) {
            element.set('rubricId', targetRubric);
          }
          await element.save();
        }

        let columns = element.get('_columns') || [];
        for (let col of columns) {
          for (let criteria of col.criteria || []) {
            if (!criteria.get('hasDirtyAttributes')) continue;
            if (criteria.get('isNew')) {
              criteria.setProperties({
                rubricId: targetRubric,
                elementId: element,
              });
            }
            await criteria.save();
          }
        }
      }

      // Persist deletions: rubric-element / rubric-criteria records that
      // were removed during this editing session were marked with
      // deleteRecord() in removeElement / removeCriteria; save them here to
      // persist the DELETE. Scoped to this rubric so unrelated deletions
      // in the store aren't touched.
      let rubricId = rubric.get('id');
      let deletedCriteria = this.store
        .peekAll('reports/rubric-criteria')
        .filter(
          (c) =>
            c.get('isDeleted') && c.belongsTo('rubricId').id() === rubricId,
        );
      for (let c of deletedCriteria) {
        await c.save();
      }
      let deletedElements = this.store
        .peekAll('reports/rubric-element')
        .filter(
          (e) =>
            e.get('isDeleted') && e.belongsTo('rubricId').id() === rubricId,
        );
      for (let el of deletedElements) {
        await el.save();
      }

      // Versioning temporarily disabled — old flow created brand-new
      // element + criteria records on the (possibly newly versioned)
      // rubric. Restore when versioning returns:
      //   for (let element of this.editingElements.toArray()) {
      //     let newElement = this.store.createRecord('reports/rubric-element', {
      //       rubricId: targetRubric,
      //       name: element.get('name'),
      //       description: element.get('description'),
      //       sequence: element.get('sequence'),
      //       categoryId: element.get('categoryId'),
      //       templateCategoryId: element.get('templateCategoryId'),
      //     });
      //     await newElement.save();
      //     let columns = element.get('_columns') || [];
      //     for (let col of columns) {
      //       for (let criteria of (col.criteria || []).toArray()) {
      //         let newCriteria = this.store.createRecord(
      //           'reports/rubric-criteria',
      //           {
      //             rubricId: targetRubric,
      //             elementId: newElement,
      //             progressColumn: criteria.get('progressColumn'),
      //             sequence: criteria.get('sequence'),
      //             text: criteria.get('text'),
      //             achievedNarrative: criteria.get('achievedNarrative'),
      //             helpAtHome: criteria.get('helpAtHome'),
      //             nextStep: criteria.get('nextStep'),
      //             stretchNextStep: criteria.get('stretchNextStep'),
      //           }
      //         );
      //         await newCriteria.save();
      //       }
      //     }
      //   }
      saved = true;
    } catch (e) {
      // The API explains rubric family conflicts (e.g. another rubric in
      // the family already covers this subject and milestone)
      this.toaster.error(apiErrorDetail(e, 'Failed to save rubric.'));
    }
    // Versioning temporarily disabled — the original `finally` block
    // cleaned up the isNew stand-in records that were never saved (since
    // the versioning flow created fresh records on the new rubric
    // version). In-place save uses the editingElements records directly,
    // so they're real on success and remain dirty on failure for retry.
    // Restore when versioning returns:
    //   } finally {
    //     for (let element of this.editingElements.toArray()) {
    //       let columns = element.get('_columns') || [];
    //       columns.forEach((col) => {
    //         (col.criteria || []).forEach((c) => {
    //           if (c.get('isNew')) c.deleteRecord();
    //         });
    //       });
    //       if (element.get('isNew')) element.deleteRecord();
    //     }
    //   }

    if (!saved) return;

    this.toaster.success('Rubric saved successfully.');

    this.loadRubric(targetRubric);
    // Versioning temporarily disabled — without a `next` pointer there's no
    // new version to redirect to. Restore when versioning returns:
    //   if (next) {
    //     this.router.transitionTo('rubrics.rubric', next);
    //   } else {
    //     this.loadRubric(targetRubric);
    //   }
  }

  @action
  backToLanding() {
    // In-place save: mutations on existing records are live in the store,
    // so cancelling must also roll back dirty attributes and un-mark
    // pending deletions. Original cleanup only had to discard isNew
    // stand-ins because the versioning flow saved into fresh records.
    // Restore when versioning returns:
    //   for (let element of this.editingElements.toArray()) {
    //     let columns = element.get('_columns') || [];
    //     columns.forEach((col) => {
    //       (col.criteria || []).forEach((c) => {
    //         if (c.get('isNew')) c.deleteRecord();
    //       });
    //     });
    //     if (element.get('isNew')) element.deleteRecord();
    //   }
    let rubricId = this.get('editingRubric.id');
    let rollback = (records) => {
      records.forEach((r) => {
        if (r.get('isNew')) {
          r.deleteRecord();
        } else if (r.get('isDeleted') || r.get('hasDirtyAttributes')) {
          r.rollbackAttributes();
        }
      });
    };
    if (rubricId) {
      let elements = this.store
        .peekAll('reports/rubric-element')
        .filter((e) => e.belongsTo('rubricId').id() === rubricId);
      let criteria = this.store
        .peekAll('reports/rubric-criteria')
        .filter((c) => c.belongsTo('rubricId').id() === rubricId);
      rollback(criteria);
      rollback(elements);
    }
    this.router.transitionTo('rubrics');
  }
}
